import { Button } from "@eisox/design-system";
import { CalendarIcon } from "@eisox/icons";
import { useQuery } from "@tanstack/react-query";

import { airHandlerUnitApi } from "~/api";
import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { DisableLink } from "./disable-link";

interface PlanningTabButtonProps {
  buildingId: string;
  airHandlerUnitId: string;
  mode: Mode;
  /** Live feed unavailable (stale/frozen) — the gateway round-trip can't complete, so keep it disabled. */
  isRealtimeStale: boolean;
}

/**
 * Planning tab entry, gated like the legacy `LivePlanningsGate`: hidden outside Live mode, and disabled
 * until the plannings query (a gateway round-trip) has resolved. The query is `enabled` only when Live is
 * available, so it fires on mount and re-fires automatically whenever Live becomes available again.
 */
export const PlanningTabButton: React.FC<PlanningTabButtonProps> = ({
  buildingId,
  airHandlerUnitId,
  mode,
  isRealtimeStale,
}) => {
  const liveAvailable = mode === Mode.Live && !isRealtimeStale;

  const { data } = useQuery({
    ...airHandlerUnitApi.queries.plannings(airHandlerUnitId),
    enabled: liveAvailable,
    // The gateway round-trip can be slow; keep it off the global loading dialog.
    meta: { skipGlobalLoader: true },
  });

  if (mode === Mode.History) return <div className="ml-auto" />;

  const disabled = data === undefined;

  return (
    <Button asChild disabled={disabled} className="ml-auto">
      <DisableLink
        disabled={disabled}
        to={"/buildings/$buildingId/air-handler-units/$airHandlerUnitId/settings/planning"}
        params={{ buildingId, airHandlerUnitId }}
      >
        <CalendarIcon />
        {m.planning()}
      </DisableLink>
    </Button>
  );
};
